// src/components/seo.component.js

import React from "react";
import { Helmet } from "react-helmet-async";
import constants from "../constants/constants";
import pageSEO from "../constants/pageSEO";
import { useUserParams } from "../hooks/useUserParams";

/**
 * Unified SEO configuration component
 * Can be referenced in each page to set page-specific meta tags
 *
 * @param {Object} props Component properties
 * @param {string} props.title Page title
 * @param {string} props.description Page description
 * @param {string} props.keywords Page keywords
 * @param {string} props.path Current page path (without domain)
 * @param {string} props.image Social sharing image address
 * @param {string} props.type Open Graph type
 * @param {React.ReactNode} props.children Can pass in additional head tags, such as structured data
 */
const SEO = ({
  title,
  description,
  keywords,
  path,
  image,
  type = "website",
  children,
}) => {
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentLogo,
    getCurrentAbout,
  } = useUserParams();

  // build the full title
  const brokerageName = getCurrentBrokerage();
  const agentName = getCurrentAgentName();
  const fullTitle = title
    ? `${title} | ${brokerageName}`
    : `${agentName} | ${brokerageName}`;

  // build the full site URL
  const baseUrl =
    constants.ENVIRONMENT === "production"
      ? `https://${constants.DOMAIN}`
      : `http://${constants.URL}`;

  // build the normalized URL
  const url = path ? `${baseUrl}/${path.replace(/^\//, "")}` : baseUrl;

  // handle the image URL
  const logo = getCurrentLogo();
  const imageUrl = image
    ? image.startsWith("http")
      ? image
      : `${baseUrl}${image}`
    : logo && (logo.startsWith("http") || logo.startsWith("data:"))
    ? logo
    : `${baseUrl}/logo.png`;

  const metaDescription = description || getCurrentAbout();
  const metaKeywords =
    keywords ||
    `${agentName}, ${brokerageName}, ${getCurrentAgentTitle()}, real estate`;

  return (
    <Helmet>
      {/* basic meta data */}
      <title>{fullTitle}</title>
      {metaDescription && <meta name="description" content={metaDescription} />}
      {metaKeywords && <meta name="keywords" content={metaKeywords} />}

      {/* normalize the link */}
      <link rel="canonical" to={url} />

      {/* Open Graph tags - for social media sharing */}
      <meta property="og:title" content={fullTitle} />
      {metaDescription && (
        <meta property="og:description" content={metaDescription} />
      )}
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={brokerageName} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {metaDescription && (
        <meta name="twitter:description" content={metaDescription} />
      )}
      <meta name="twitter:image" content={imageUrl} />

      {/* mobile device meta data */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* robots instructions */}
      <meta name="robots" content="index, follow" />

      {/* other custom meta tags */}
      {children}
    </Helmet>
  );
};

/**
 * Default SEO configuration component
 * Used in App.js to provide default SEO configuration for all pages
 */
export const DefaultSEO = () => {
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentLogo,
    getCurrentAbout,
  } = useUserParams();

  const baseUrl =
    constants.ENVIRONMENT === "production"
      ? `https://${constants.DOMAIN}`
      : `http://${constants.URL}`;

  const brokerageName = getCurrentBrokerage();
  const agentName = getCurrentAgentName();
  const defaultDescription = getCurrentAbout() ||
    `${brokerageName} provides professional real estate services to help you buy and sell properties.`;
  const defaultLogo = (() => {
    const logo = getCurrentLogo();
    if (!logo) return `${baseUrl}/logo.png`;
    if (logo.startsWith("http") || logo.startsWith("data:")) {
      return logo;
    }
    return `${baseUrl}${logo}`;
  })();

  return (
    <Helmet>
      {/* default title and description */}
      <title>{brokerageName} - Real Estate Services</title>
      <meta name="description" content={defaultDescription} />
      <meta
        name="keywords"
        content={`real estate, realtor, ${agentName}, ${brokerageName}, property, home, house, real estate services`}
      />

      {/* default OG tags */}
      <meta
        property="og:title"
        content={`${brokerageName} - Real Estate Services`}
      />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={baseUrl} />
      <meta property="og:image" content={defaultLogo} />
      <meta property="og:site_name" content={brokerageName} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:title"
        content={`${brokerageName} - Real Estate Services`}
      />
      <meta name="twitter:description" content={defaultDescription} />
      <meta name="twitter:image" content={defaultLogo} />

      {/* basic meta data */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta charSet="UTF-8" />
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />

      {/* website icon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo192.png" />

      {/* normalize the link */}
      <link rel="canonical" href={baseUrl} />

      {/* brokerage structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          name: brokerageName,
          telephone: getCurrentAgentNumber(),
          email: getCurrentAgentEmail(),
          url: baseUrl,
          logo: defaultLogo,
          description: defaultDescription,
          address: {
            "@type": "PostalAddress",
            addressCountry: "CA",
          },
          sameAs: Object.values(constants.SOCIALS).filter(
            (url) => url && url.length > 0
          ),
        })}
      </script>
    </Helmet>
  );
};

export { pageSEO };
export default SEO;
